import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../i18n';
import { api, sha256Hex, uploadToSignedUrl } from '../../lib/api';

export type MediaUploaderHandle = {
  open: () => void;
  /** Upload files dropped onto the page shell. */
  uploadFiles: (files: FileList | File[]) => void;
};

type MediaUploaderProps = {
  organisationId: string;
  /** Called with the asset ids that finished uploading, in upload order. */
  onUploaded: (assetIds: string[]) => void;
};

async function uploadOne(organisationId: string, file: File): Promise<string> {
  const kind = file.type.startsWith('video/') ? 'video' : 'image';
  const created = await api.createMediaAsset(organisationId, {
    kind,
    originalFilename: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  await uploadToSignedUrl(created.uploadUrl, file);
  const checksumSha256 = await sha256Hex(file);
  await api.completeMediaAsset(organisationId, created.assetId, {
    checksumSha256,
    byteSize: file.size,
  });
  return created.assetId;
}

function isMediaFile(file: File): boolean {
  return /^(image|video)\//.test(file.type);
}

/**
 * Content dropzone — matches Ghost Screen Content.dc.html upload strip.
 */
export const MediaUploader = forwardRef<MediaUploaderHandle, MediaUploaderProps>(
  function MediaUploader({ organisationId, onUploaded }, ref) {
    const { t } = useTranslation(['content', 'common']);
    const inputRef = useRef<HTMLInputElement>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const uploadFiles = async (list: FileList | File[]) => {
      const files = Array.from(list).filter(isMediaFile);
      if (!files.length || busy) return;

      setBusy(true);
      setError(null);
      const uploadedIds: string[] = [];
      try {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i]!;
          setStatus(
            files.length === 1
              ? t('uploader.uploadingOne', { name: file.name })
              : t('uploader.uploadingMany', { index: i + 1, total: files.length, name: file.name }),
          );
          uploadedIds.push(await uploadOne(organisationId, file));
        }
        setStatus(t('uploader.complete', { count: files.length }));
      } catch (err) {
        setStatus(null);
        setError(apiErrorMessage(err, t('uploader.error')));
      } finally {
        if (uploadedIds.length > 0) onUploaded(uploadedIds);
        setBusy(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        if (!busy) inputRef.current?.click();
      },
      uploadFiles: (files) => {
        void uploadFiles(files);
      },
    }));

    return (
      <div
        className={[
          'flex flex-wrap items-center gap-x-[18px] gap-y-3.5 rounded-[14px] border-[1.5px] border-dashed px-5 py-[18px]',
          busy ? 'opacity-80' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          borderColor: 'var(--gs-line)',
          background: 'var(--gs-panel)',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          disabled={busy}
          className="sr-only"
          onChange={(e) => {
            if (e.target.files?.length) void uploadFiles(e.target.files);
          }}
        />

        <span
          className="grid h-11 w-11 shrink-0 place-items-center rounded-[11px] border"
          style={{
            background: 'var(--gs-panel2)',
            borderColor: 'var(--gs-line)',
            color: 'var(--gs-ink2)',
          }}
          aria-hidden
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
          </svg>
        </span>

        <span className="flex w-full min-w-0 flex-1 flex-col gap-0.5 sm:min-w-[240px]">
          <span className="text-[15.5px] font-extrabold" style={{ color: 'var(--gs-ink)' }}>
            {busy ? t('uploader.titleBusy') : t('uploader.title')}
          </span>
          <span
            className="text-[13.5px] leading-[1.45]"
            style={{ color: 'var(--gs-muted)' }}
          >
            {busy ? (status ?? t('common:actions.working')) : t('uploader.hint')}
          </span>
          {error ? (
            <span className="text-sm font-medium" style={{ color: 'var(--gs-bad)' }} role="alert">
              {error}
            </span>
          ) : null}
          {!busy && !error && status ? (
            <span className="text-sm font-medium" style={{ color: 'var(--gs-ok)' }}>
              {status}
            </span>
          ) : null}
        </span>

        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="shrink-0 rounded-[10px] px-5 py-[11px] text-sm font-bold disabled:opacity-60"
          style={{
            color: 'var(--gs-accent-ink)',
            background: 'var(--gs-accent-solid)',
          }}
        >
          {t('uploader.choose')}
        </button>
      </div>
    );
  },
);
